import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chip } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppTable from "../../../shared/AppTable";
import AppButton from "../../../shared/AppButton";
import { API_BASE_URL } from "../../../config/apiConfig";
import CustomFilterInput from "../../../shared/CustomFilterInput";

const API_URL = `${API_BASE_URL}/claim/rejection-report`;

const authConfig = (responseType) => ({
  ...(responseType ? { responseType } : {}),
  headers: { Authorization: `Bearer ${localStorage.getItem("token")?.replaceAll('"', "")}` },
});

const dateOnly = (value) => (value ? new Date(value).toLocaleDateString() : "---");
const normalizeId = (value) => String(value || "").trim();

export default function ClaimRejectionReportMain() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", payer_id: "" });

  const payerGroups = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("payersData") || "[]");
    } catch {
      return [];
    }
  }, []);

  const payers = useMemo(
    () =>
      payerGroups.flatMap((group) =>
        (group.payers || []).map((payer) => ({
          ...payer,
          tpaName: group.name_en,
        })),
      ),
    [payerGroups],
  );

  const getPayerNameById = useCallback(
    (id) => payers.find((item) => normalizeId(item.id) === normalizeId(id))?.name_en,
    [payers],
  );

  const getReportPayerLabel = useCallback(
    (row) => {
      const payerName = getPayerNameById(row.payer_id);
      if (payerName) return payerName;
      if (!normalizeId(row.payer_id)) return t("All Payers");
      return row.payer_name || row.payer_id || "---";
    },
    [getPayerNameById, t],
  );

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL, authConfig());
      setReports(response.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || t("Unable to load claim rejection reports"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const refreshStatus = useCallback(async (id, silent = false) => {
    try {
      const response = await axios.get(`${API_URL}/status`, { ...authConfig(), params: { id } });
      const report = response.data?.data;
      if (report) setReports((current) => current.map((item) => (item.id === id ? report : item)));
      if (!silent && report?.status) toast.success(t(`Report status: ${report.status}`));
      return report;
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.message || t("Unable to refresh report status"));
      return null;
    }
  }, [t]);

  useEffect(() => {
    const active = reports.filter((report) => ["queued", "processing"].includes(report.status));
    if (!active.length) return undefined;

    const timer = setInterval(() => {
      active.forEach((report) => refreshStatus(report.id, true));
    }, 4000);

    return () => clearInterval(timer);
  }, [refreshStatus, reports]);

  const generateReport = async (event) => {
    event.preventDefault();

    if (!filters.from_date || !filters.to_date) {
      toast.error(t("From Date and To Date are required"));
      return;
    }
    if (filters.from_date > filters.to_date) {
      toast.error(t("From Date cannot be greater than To Date"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        from_date: filters.from_date,
        to_date: filters.to_date,
        ...(normalizeId(filters.payer_id) ? { payer_id: filters.payer_id } : {}),
        format: "excel",
      };

      await axios.post(API_URL, payload, authConfig());
      toast.success(t("Report generation started"));
      await fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t("Unable to generate report"));
    } finally {
      setSubmitting(false);
    }
  };

  const download = useCallback(async (report) => {
    try {
      const response = await axios.get(`${API_URL}/file`, { ...authConfig("blob"), params: { id: report.id } });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `claim-rejection-report-${report.id}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || t("Report file is not ready"));
    }
  }, [t]);

  const columns = useMemo(
    () => [
      { header: t("Report ID"), render: (row) => row.id },
      { header: t("Payer"), render: (row) => getReportPayerLabel(row) },
      { header: t("From Date"), render: (row) => dateOnly(row.from_date) },
      { header: t("To Date"), render: (row) => dateOnly(row.to_date) },
      {
        header: t("Status"),
        render: (row) => (
          <Chip
            size="small"
            label={row.status || "---"}
            color={row.status === "completed" ? "success" : row.status === "failed" ? "error" : "info"}
          />
        ),
      },
      { header: t("Created At"), render: (row) => dateOnly(row.created_at) },
      { header: t("Error"), render: (row) => row.error || "---" },
      {
        header: t("Actions"),
        render: (row) => (
          <div className="flex items-center gap-2">
            <div
              title={t("Refresh Status")}
              role="button"
              tabIndex={0}
              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
              onClick={() => refreshStatus(row.id)}
              onKeyDown={(e) => e.key === "Enter" && refreshStatus(row.id)}
            >
              <RefreshIcon sx={{ fontSize: 19 }} />
            </div>
            <div
              title={t("Download")}
              role="button"
              tabIndex={row.status === "completed" ? 0 : -1}
              className={`flex items-center justify-center transition-colors ${
                row.status === "completed" ? "cursor-pointer text-gray-500 hover:text-blue-600" : "cursor-default text-gray-300"
              }`}
              onClick={() => row.status === "completed" && download(row)}
              onKeyDown={(e) => e.key === "Enter" && row.status === "completed" && download(row)}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 19 }} />
            </div>
          </div>
        ),
      },
    ],
    [download, getReportPayerLabel, refreshStatus, t],
  );

  return (
    <div className="pre_auth_container !p-0 flex flex-col h-full">
      <div className="mb-2 border-b border-slate-200 pb-2 flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-slate-900">{t("Claim Rejection Report")}</h1>
          <p className="mt-0.5 text-[12px] text-slate-500">{t("Download rejected claims with item rejection reasons")}</p>
        </div>
      </div>
      <form onSubmit={generateReport} className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="flex flex-wrap items-end gap-1.5">
          <div className="w-full sm:w-[150px]">
            <CustomFilterInput
              label={t("From Date")}
              name="from_date"
              type="date"
              required
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div className="w-full sm:w-[150px]">
            <CustomFilterInput
              label={t("To Date")}
              name="to_date"
              type="date"
              required
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div className="w-full sm:w-[280px]">
            <CustomFilterInput
              label={t("Payer")}
              name="payer_id"
              isSelect
              value={filters.payer_id}
              onChange={(e) => setFilters({ ...filters, payer_id: e.target.value })}
              placeholder={t("All Payers")}
              renderOptions={() =>
                payers.map((item) => (
                  <option key={`${item.tpaName || "tpa"}-${item.id}`} value={item.id}>
                    {`${item.name_en}${item.tpaName ? ` (${item.tpaName})` : ""}`}
                  </option>
                ))
              }
            />
          </div>
          <div className="flex gap-1.5">
            <AppButton type="submit" label={submitting ? t("Generating...") : t("Generate")} disabled={submitting} />
            <AppButton
              type="button"
              label={t("Refresh")}
              variant="outlined"
              startIcon={<RefreshIcon className="!text-[13px]" />}
              onClick={fetchReports}
            />
          </div>
        </div>
      </form>
      <div className="flex-1 overflow-auto">
        <AppTable columns={columns} data={reports} loading={loading} actions={false} />
      </div>
    </div>
  );
}
